import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TMap } from '@shared/types';

const useSearchParams = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const refLocation = useRef(location);
  const refNavigate = useRef(navigate);
  refLocation.current = location;
  refNavigate.current = navigate;

  // Array value → replace with repeated params (key=a&key=b). Empty array or undefined → delete.
  const changeMany = useCallback(
    (map: TMap<string, string | string[] | undefined>, { replace = true }: { replace?: boolean } = {}) => {
      const newSearchParams = new URLSearchParams(refLocation.current.search);
      for (const key in map) {
        const value = map[key];
        newSearchParams.delete(key);
        if (Array.isArray(value)) {
          value.forEach((v) => newSearchParams.append(key, v));
        } else if (value) {
          newSearchParams.set(key, value);
        }
      }
      const search = newSearchParams.toString();
      refNavigate.current(
        {
          pathname: refLocation.current.pathname,
          search: search ? `?${search}` : '',
        },
        { replace }
      );
    },
    []
  );

  const change = useCallback(
    (key: string, value?: string, options?: { replace?: boolean }) => changeMany({ [key]: value }, options),
    [changeMany]
  );

  return {
    searchParams,
    change,
    changeMany,
  };
};

export { useSearchParams };
